import React from "react";
import { Box } from "@mui/material";
import { TreatmentPlanStep } from "../../../../services/treatmentPlanService";
import { deduplicateCdtCodesStructured, deduplicateCdtDescriptions } from "../../../../utils/cdtCodeUtils";
import {
  StepCard,
  StepContent,
  StepTitle,
  StepDetails,
  StepDetailItem,
  StepDetailLabel,
  StepDetailValue,
  StepDetailHint,
} from "./TreatmentStep.styled";

interface TreatmentStepProps {
  step: TreatmentPlanStep;
}

/**
 * Treatment step (procedure) card component
 * Used within appointments to display individual procedures
 */
const TreatmentStep: React.FC<TreatmentStepProps> = ({ step }) => {
  // Get fee - use step.fee if available, otherwise calculate from code suggestions
  const fee =
    step.fee != null
      ? step.fee
      : step.codeSuggestions.reduce(
          (sum, code) => sum + (code.defaultFee || 0),
          0,
        );

  // Get duration - use step.durationMinutes if available, otherwise default 20
  const duration = step.durationMinutes ?? 20;

  const cdtCodeEntries = deduplicateCdtCodesStructured(step.codeSuggestions);
  const cdtCodesNode: React.ReactNode = cdtCodeEntries.length === 0 ? "-" : (
    <>
      {cdtCodeEntries.map((entry, i) => (
        <React.Fragment key={entry.code}>
          {i > 0 && ", "}
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            {entry.code}
            {entry.count > 1 && (
              <Box component="span" sx={{ color: "#6B7A94" }}> x{entry.count}</Box>
            )}
          </Box>
        </React.Fragment>
      ))}
    </>
  );

  // Get explanation from CDT code descriptions (deduplicated with multiplier)
  const explanation = deduplicateCdtDescriptions(step.codeSuggestions).join("\n");

  return (
    <StepCard>
      <StepContent>
        <StepTitle>{step.name}</StepTitle>
        <StepDetails>
          <StepDetailItem sx={{ flex: 1 }}>
            <StepDetailValue sx={{ fontWeight: 400, fontSize: "14px" }}>
              {explanation || "-"}
            </StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>CDT Code:</StepDetailLabel>
            <StepDetailValue>{cdtCodesNode}</StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>Duration:</StepDetailLabel>
            <StepDetailValue>{duration} min</StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>Fee:</StepDetailLabel>
            <StepDetailValue>
              ${fee.toLocaleString("en-US")}{" "}
              <StepDetailHint>(based on clinic rates)</StepDetailHint>
            </StepDetailValue>
          </StepDetailItem>
        </StepDetails>
      </StepContent>
    </StepCard>
  );
};

export default TreatmentStep;
